import getLang from 'I18n/utils/getLang';
import { parse as parseParams } from 'hub-http/helpers/params';
import useDetails from 'marketplace-ui-apps-core/details/hooks/useDetails';
import useDetailsPreview from 'marketplace-ui-apps-core/details/hooks/useDetailsPreview';
import ListingView from 'marketplace-ui-apps-core/details/records/ListingView';
import { useUnifiedDetails } from 'marketplace-ui-details/hooks/useUnifiedDetails';
import { useCallback, useMemo } from 'react';
import { useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';

// @ts-expect-error untyped dependency
import { useParsedPathParams } from './useParsedPathParams';
export function useListingView(isUngatedForUnifiedDetails) {
  const {
    path
  } = useRouteMatch();
  const appContext = useAppContext();
  const {
    search
  } = useLocation();
  const params = useParams();
  const {
    published,
    language
  } = parseParams(search.slice(1));
  const isPreview = published === 'false' || path === '/_preview/:offeringId' || appContext === 'admin-preview';
  const {
    offeringId,
    slug
  } = useParsedPathParams(params);

  // We can't use the Unified Details CHIRP endpoint in the public marketplace.
  // Because we're rolling it out behind a gate, and a public user will always be
  // !isUngatedForUnifiedDetails, this logic still works, but if we remove the gate,
  // we'll need to account for public users.
  const {
    unifiedDetailsData,
    unifiedDetailsLoading,
    unifiedDetailsError
  } = useUnifiedDetails({
    slug,
    language: getLang(),
    skip: isPreview || !isUngatedForUnifiedDetails
  });
  const {
    detailsData,
    detailsLoading,
    detailsError
  } = useDetails({
    appContext,
    slug,
    skip: isPreview || isUngatedForUnifiedDetails
  });
  const {
    detailsPreviewData,
    detailsPreviewLoading,
    detailsPreviewError
  } = useDetailsPreview({
    appContext,
    offeringId,
    skip: !isPreview,
    language
  });
  const appDetails = useMemo(() => {
    if (isPreview) {
      return Object.assign({}, detailsPreviewData === null || detailsPreviewData === void 0 ? void 0 : detailsPreviewData.appDetails);
    } else if (isUngatedForUnifiedDetails) {
      return Object.assign({}, unifiedDetailsData);
    } else {
      return Object.assign({}, detailsData === null || detailsData === void 0 ? void 0 : detailsData.appDetails);
    }
  }, [isPreview, detailsPreviewData, detailsData, unifiedDetailsData, isUngatedForUnifiedDetails]);
  const listingView = useMemo(() => {
    return new ListingView(Object.assign({}, appDetails));
  }, [appDetails]);
  const getListingViewEventProperties = useCallback(() => {
    return {
      applicationId: listingView.getAppId(),
      applicationName: listingView.name,
      category: listingView.categoryV2,
      certification: listingView.getIsCertified(),
      contentLanguage: listingView.language,
      homemade: listingView.offering.homemade,
      offeringId: listingView.offeringId
    };
  }, [listingView]);
  return {
    appDetails,
    listingView,
    loading: isPreview ? detailsPreviewLoading : isUngatedForUnifiedDetails ? unifiedDetailsLoading : detailsLoading,
    error: isPreview ? detailsPreviewError : isUngatedForUnifiedDetails ? unifiedDetailsError : detailsError,
    getListingViewEventProperties
  };
}